// The web app's failures, reported to the server's log (Poulpe-706): uncaught
// errors, unhandled rejections, and API requests that failed with a 5xx or no
// answer. The server bounds and limits reports again; these bounds keep the
// web app from sending what it need not.
//
// What a report holds:
// - the page's path, never its query or fragment;
// - for a failed request, its method, path (no query), status and
//   `x-request-id`; never its body nor the server's message;
// - for an uncaught Error, its name, message and the first lines of its stack.
//   A message is free text and can quote what a person typed (a parser's
//   error may echo its input); the server caps and redacts it again;
// - for a rejection or an error that is not an Error, nothing but its kind.
// An ApiError is not reported as a rejection: httpApi's onFailure reported it
// already when it was a failure, and a 4xx is the API saying no.
import { ApiError, type RequestFailure } from '../api/client'
import type { WebReport } from '../api/types'

/** How many reports the web app sends in a minute. */
export const PER_MINUTE = 10
const MINUTE_MS = 60_000
const STACK_LINES = 10

const CAPS: Record<'page' | 'name' | 'message' | 'stack' | 'method' | 'path', number> = {
  page: 200,
  name: 60,
  message: 300,
  stack: 1500,
  method: 10,
  path: 200,
}

type Draft = Omit<WebReport, 'page'>

/** A path without its query or fragment. */
function pathOnly(text: string): string {
  return text.split(/[?#]/, 1)[0]!
}

/** The report within its bounds: each text cut to its cap, paths without queries. */
export function bounded(report: WebReport): WebReport {
  const out: WebReport = { ...report, page: pathOnly(report.page) }
  if (out.path !== undefined) out.path = pathOnly(out.path)
  for (const key of Object.keys(CAPS) as (keyof typeof CAPS)[]) {
    const text = out[key]
    if (text !== undefined) out[key] = Array.from(text).slice(0, CAPS[key]).join('')
  }
  return out
}

/** What an uncaught error or a rejection reports; null for an ApiError. */
export function errorReport(kind: 'error' | 'rejection', value: unknown): Draft | null {
  if (value instanceof ApiError) return null
  if (!(value instanceof Error)) return { kind }
  const stack = value.stack?.split('\n').slice(0, STACK_LINES).join('\n')
  return { kind, name: value.name, message: value.message, ...(stack ? { stack } : {}) }
}

/** What a failed request reports. */
export function requestReport({ method, path, status, requestId }: RequestFailure): Draft {
  return { kind: 'request', method, path: pathOnly(path), status, ...(requestId ? { request_id: requestId } : {}) }
}

export interface ReporterOptions {
  send: (report: WebReport) => Promise<void>
  /** The page's path; `location.pathname` by default. */
  page?: () => string
  now?: () => number
}

/**
 * A function that sends a report, at most PER_MINUTE in a minute, and not
 * the same one twice in a minute. It swallows a send's failure, which the
 * client never reports, so a report never reports itself.
 */
export function reporter({ send, page = () => window.location.pathname, now = Date.now }: ReporterOptions): (draft: Draft) => void {
  let windowStart = Number.NEGATIVE_INFINITY
  let count = 0
  const seen = new Set<string>()
  return (draft) => {
    const at = now()
    if (at - windowStart >= MINUTE_MS) {
      windowStart = at
      count = 0
      seen.clear()
    }
    const report = bounded({ ...draft, page: page() })
    const key = JSON.stringify(report)
    if (count >= PER_MINUTE || seen.has(key)) return
    count += 1
    seen.add(key)
    send(report).catch(() => {})
  }
}

/** Reports a target's uncaught errors and unhandled rejections; answers how to stop. */
export function listenForErrors(target: Pick<Window, 'addEventListener' | 'removeEventListener'>, report: (draft: Draft) => void): () => void {
  const onError = (event: ErrorEvent) => {
    const draft = errorReport('error', event.error)
    if (draft) report(draft)
  }
  const onRejection = (event: PromiseRejectionEvent) => {
    const draft = errorReport('rejection', event.reason)
    if (draft) report(draft)
  }
  target.addEventListener('error', onError)
  target.addEventListener('unhandledrejection', onRejection)
  return () => {
    target.removeEventListener('error', onError)
    target.removeEventListener('unhandledrejection', onRejection)
  }
}
